'use client';

import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function UrgencyOfferSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [unitsLeft, setUnitsLeft] = useState(47);

  const productUrl = 'https://www.lavyre.com/cart';

  const handleBuyNow = () => {
    try {
      window.open(productUrl, '_blank');
    } catch (error) {
      console.error('Failed to open product URL:', error);
    }
  };

  // Simulate decreasing units (optional)
  useEffect(() => {
    const interval = setInterval(() => {
      setUnitsLeft((prev) => Math.max(30, prev - 1));
    }, 120000); // Decrease every 2 minutes

    return () => clearInterval(interval);
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-br from-red-50 to-orange-50"
    >
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Urgency Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block bg-red-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full text-xs sm:text-sm md:text-base font-medium mb-4 sm:mb-6"
          >
            ⚡ Limited Time Offer
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-4 sm:mb-6"
          >
            50% Off Today Only
          </motion.h2>

          {/* Price */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-2">
              <span className="text-black/40 text-2xl sm:text-3xl md:text-4xl lg:text-5xl line-through font-serif">
                $149.99
              </span>
              <span className="text-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif">
                $49.90
              </span>
            </div>
            <p className="text-red-600 text-xs sm:text-sm md:text-base font-medium">
              This deal may end at any time due to high demand.
            </p>
          </motion.div>

          {/* Warning */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white rounded-lg p-4 sm:p-6 md:p-8 shadow-lg mb-6 sm:mb-8"
          >
            <p className="text-black/80 text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-6 leading-relaxed">
              We're limiting this promotion because we can't keep up with orders at this price. Once our current inventory sells out, the price goes back to $149.99.
            </p>

            {/* What You Save */}
            <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              <div className="flex items-center justify-between text-xs sm:text-sm md:text-base">
                <span className="text-black/70">✅ $100 off retail price</span>
                <span className="font-medium text-black">$100.00</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm md:text-base">
                <span className="text-black/70">✅ Free US shipping</span>
                <span className="font-medium text-black">$9.95</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm md:text-base">
                <span className="text-black/70">✅ Free premium gift box</span>
                <span className="font-medium text-black">$5.00</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm md:text-base">
                <span className="text-black/70">✅ 30-day guarantee included</span>
                <span className="font-medium text-black">Priceless</span>
              </div>
              <div className="border-t border-black/10 pt-2 sm:pt-3 flex items-center justify-between text-sm sm:text-base md:text-lg font-medium">
                <span className="text-black">Total Value:</span>
                <span className="text-black">$114.95+</span>
              </div>
            </div>

            {/* Units Left */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4"
            >
              <p className="text-red-600 text-xs sm:text-sm md:text-base font-medium">
                ⚠️ Only <strong>{unitsLeft} units</strong> left at this price.
              </p>
            </motion.div>
          </motion.div>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBuyNow}
            className="w-full max-w-md mx-auto bg-black text-white py-4 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-medium hover:bg-black/90 transition-all whitespace-nowrap cursor-pointer active:scale-[0.98] shadow-xl"
          >
            ADD TO CART — $49.90
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
